import { useState, useEffect, useRef } from 'react';
import '../styles/EditorProducto.css';

// Cada item del array imagenes tiene la forma:
//   { tipo: 'existente', url: string }
// | { tipo: 'nueva',    file: File, preview: string }

const EditorProducto = ({ mueble, isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    nombre:      mueble?.nombre      || '',
    descripcion: mueble?.descripcion || '',
    imagen:      mueble?.imagen      || '',
    categoria:   mueble?.categoria   || '',
    proveedor:   mueble?.proveedor   || '',
  });

  const [imagenes, setImagenes] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const fileInputRef    = useRef(null);
  const dragIndex       = useRef(null);
  const dragOverIndex   = useRef(null);
  // Registra todos los object URLs creados para revocarlos al desmontar
  const createdPreviews = useRef([]);

  // Cargar categorías y proveedores al abrir
  useEffect(() => {
    if (!isOpen) return;
    fetch('/api/categorias')
      .then(r => r.json())
      .then(data => setCategorias(Array.isArray(data) ? data : []))
      .catch(() => setCategorias([]));
    fetch('/api/proveedores')
      .then(r => r.json())
      .then(data => setProveedores(Array.isArray(data) ? data : []))
      .catch(() => setProveedores([]));
  }, [isOpen]);

  // Sincronizar formulario con el producto al abrir
  useEffect(() => {
    if (isOpen && mueble) {
      setFormData({
        nombre:      mueble.nombre      || '',
        descripcion: mueble.descripcion || '',
        imagen:      mueble.imagen      || '',
        categoria:   mueble.categoria   || '',
        proveedor:   mueble.proveedor   || '',
      });
      setImagenes((mueble.imagenes || []).map(url => ({ tipo: 'existente', url })));
      setError('');
      setSuccess('');
      setUploadProgress('');
    }
  }, [isOpen, mueble]);

  // Revocar todos los object URLs creados cuando el componente se desmonta
  useEffect(() => {
    return () => {
      createdPreviews.current.forEach(url => URL.revokeObjectURL(url));
    };
  }, []);

  if (!isOpen) return null;

  // ─── Handlers de formulario ──────────────────────────────────────────────

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  // Agregar archivos al final del array
  const handleImagenesChange = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    const nuevas = files.map(file => {
      const preview = URL.createObjectURL(file);
      createdPreviews.current.push(preview);
      return { tipo: 'nueva', file, preview };
    });
    setImagenes(prev => [...prev, ...nuevas]);
    setError('');
    e.target.value = ''; // permite volver a seleccionar el mismo archivo
  };

  // ─── Manipulación del orden ───────────────────────────────────────────────

  const eliminarImagen = (idx) => {
    setImagenes(prev => {
      const item = prev[idx];
      if (item.tipo === 'nueva') URL.revokeObjectURL(item.preview);
      return prev.filter((_, i) => i !== idx);
    });
  };

  const marcarPrincipal = (idx) => {
    if (idx === 0) return;
    setImagenes(prev => {
      const arr = [...prev];
      const [item] = arr.splice(idx, 1);
      arr.unshift(item);
      return arr;
    });
  };

  const moverImagen = (idx, delta) => {
    const destino = idx + delta;
    if (destino < 0 || destino >= imagenes.length) return;
    setImagenes(prev => {
      const arr = [...prev];
      [arr[idx], arr[destino]] = [arr[destino], arr[idx]];
      return arr;
    });
  };

  // ─── Drag & Drop ─────────────────────────────────────────────────────────

  const handleDragStart = (e, idx) => {
    dragIndex.current = idx;
    e.dataTransfer.effectAllowed = 'move';
    e.currentTarget.classList.add('arrastrando');
  };

  const handleDragEnd = (e) => {
    e.currentTarget.classList.remove('arrastrando');
    // Limpiar highlight de todos los items
    document.querySelectorAll('.imagen-preview-item').forEach(el => {
      el.classList.remove('drag-over');
    });
  };

  const handleDragOver = (e, idx) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    dragOverIndex.current = idx;
    // Highlight visual
    document.querySelectorAll('.imagen-preview-item').forEach((el, i) => {
      el.classList.toggle('drag-over', i === idx && i !== dragIndex.current);
    });
  };

  const handleDrop = (e, idx) => {
    e.preventDefault();
    const from = dragIndex.current;
    document.querySelectorAll('.imagen-preview-item').forEach(el => el.classList.remove('drag-over'));
    if (from === null || from === idx) { dragIndex.current = null; return; }
    setImagenes(prev => {
      const arr = [...prev];
      const [item] = arr.splice(from, 1);
      arr.splice(idx, 0, item);
      return arr;
    });
    dragIndex.current = null;
  };

  // ─── Submit ───────────────────────────────────────────────────────────────

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    setUploadProgress('');

    try {
      if (!formData.nombre.trim()) throw new Error('El nombre no puede estar vacío');

      const tieneImagenLegacy = formData.imagen.trim() !== '';
      if (imagenes.length === 0 && !tieneImagenLegacy) {
        throw new Error('El producto debe tener al menos una imagen');
      }

      // Copia mutable para reemplazar 'nueva' → URL real
      const lista = imagenes.map(img => ({ ...img }));
      const nuevas = lista.filter(img => img.tipo === 'nueva');

      if (nuevas.length > 0) {
        setUploadProgress(`Subiendo ${nuevas.length} imagen(es) a Cloudinary...`);
        const fd = new FormData();
        nuevas.forEach(img => fd.append('imagenes', img.file));

        const uploadRes = await fetch('/api/productos/upload', { method: 'POST', body: fd });
        if (!uploadRes.ok) {
          const err = await uploadRes.json();
          throw new Error(err.error || 'Error al subir imágenes');
        }
        const { urls } = await uploadRes.json();

        // Reemplazar cada slot 'nueva' con la URL subida (en el mismo orden)
        let urlIdx = 0;
        for (let i = 0; i < lista.length; i++) {
          if (lista[i].tipo === 'nueva') {
            lista[i] = { tipo: 'existente', url: urls[urlIdx++] };
          }
        }
        setUploadProgress('');
      }

      const urlsFinales = lista.map(img => img.url);

      const dataToSend = {
        nombre:      formData.nombre.trim(),
        descripcion: formData.descripcion.trim(),
        categoria:   formData.categoria.trim(),
        proveedor:   formData.proveedor.trim(),
        imagenes:    urlsFinales,
        imagen:      urlsFinales[0] || formData.imagen.trim(),
      };

      const response = await fetch(`/api/productos/${mueble._id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(dataToSend),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al actualizar el producto');
      }

      const result = await response.json();
      setSuccess('¡Producto actualizado exitosamente!');
      setTimeout(() => onSave(result.producto), 1500);

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      setUploadProgress('');
    }
  };

  const handleCancel = () => {
    imagenes.forEach(img => {
      if (img.tipo === 'nueva') URL.revokeObjectURL(img.preview);
    });
    setFormData({
      nombre:      mueble?.nombre      || '',
      descripcion: mueble?.descripcion || '',
      imagen:      mueble?.imagen      || '',
      categoria:   mueble?.categoria   || '',
      proveedor:   mueble?.proveedor   || '',
    });
    setImagenes((mueble?.imagenes || []).map(url => ({ tipo: 'existente', url })));
    setError('');
    setSuccess('');
    setUploadProgress('');
    onClose();
  };

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="editor-modal-overlay" onClick={handleCancel}>
      <div className="editor-modal" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="editor-header">
          <h2>Editar Producto</h2>
          <button className="editor-close-btn" onClick={handleCancel} aria-label="Cerrar">✕</button>
        </div>

        <form onSubmit={handleSubmit} className="editor-form">

          {/* Nombre */}
          <div className="form-group">
            <label htmlFor="nombre">Nombre del Producto *</label>
            <input
              type="text" id="nombre" name="nombre"
              value={formData.nombre} onChange={handleChange}
              placeholder="Ej: Sillón Industrial Premium"
              disabled={loading} required
            />
          </div>

          {/* Descripción */}
          <div className="form-group">
            <label htmlFor="descripcion">Descripción</label>
            <textarea
              id="descripcion" name="descripcion"
              value={formData.descripcion} onChange={handleChange}
              placeholder="Describe el producto..." rows="3"
              disabled={loading}
            />
          </div>

          {/* Categoría */}
          <div className="form-group">
            <label htmlFor="categoria">Categoría</label>
            <select id="categoria" name="categoria" value={formData.categoria} onChange={handleChange} disabled={loading}>
              <option value="">Sin categoría</option>
              {formData.categoria && !categorias.find(c => c.nombre === formData.categoria) && (
                <option value={formData.categoria}>{formData.categoria} (heredado)</option>
              )}
              {categorias
                .filter(c => !c.parent_id)
                .sort((a, b) => a.nombre.localeCompare(b.nombre))
                .map(padre => {
                  const subs = categorias
                    .filter(c => c.parent_id === padre._id)
                    .sort((a, b) => a.nombre.localeCompare(b.nombre));
                  return (
                    <optgroup key={padre._id} label={padre.nombre}>
                      <option value={padre.nombre}>{padre.nombre}</option>
                      {subs.map(sub => (
                        <option key={sub._id} value={sub.nombre}>  ↳ {sub.nombre}</option>
                      ))}
                    </optgroup>
                  );
                })}
            </select>
          </div>

          {/* Proveedor */}
          <div className="form-group">
            <label htmlFor="proveedor">Proveedor</label>
            <select id="proveedor" name="proveedor" value={formData.proveedor || ''} onChange={handleChange} disabled={loading}>
              <option value="">Sin proveedor</option>
              {formData.proveedor && !proveedores.find(p => p.nombre === formData.proveedor) && (
                <option value={formData.proveedor}>{formData.proveedor} (heredado)</option>
              )}
              {proveedores.map(p => (
                <option key={p._id} value={p.nombre}>{p.nombre}</option>
              ))}
            </select>
          </div>

          {/* === IMÁGENES === */}
          <div className="form-group">
            <label>
              Imágenes del Producto
              {imagenes.length > 1 && (
                <span className="imagenes-hint">— arrastrá o usá ‹ › para reordenar</span>
              )}
            </label>

            {/* Grid unificado con drag & drop */}
            {imagenes.length > 0 && (
              <div className="imagenes-preview-grid">
                {imagenes.map((img, idx) => (
                  <div
                    key={idx}
                    className={[
                      'imagen-preview-item',
                      img.tipo === 'nueva'  ? 'imagen-nueva'      : '',
                      idx === 0             ? 'imagen-principal'   : '',
                    ].join(' ')}
                    draggable={!loading}
                    onDragStart={e => handleDragStart(e, idx)}
                    onDragEnd={handleDragEnd}
                    onDragOver={e => handleDragOver(e, idx)}
                    onDrop={e => handleDrop(e, idx)}
                  >
                    <img
                      src={img.tipo === 'existente' ? img.url : img.preview}
                      alt={`Imagen ${idx + 1}`}
                    />

                    {/* Badge estrella — siempre visible en la primera */}
                    {idx === 0 && (
                      <span className="imagen-badge-principal" title="Foto principal">★</span>
                    )}

                    {/* Overlay de acciones — visible al hacer hover */}
                    <div className="imagen-acciones">
                      {idx !== 0 && (
                        <button
                          type="button"
                          className="imagen-accion-btn imagen-btn-star"
                          onClick={() => marcarPrincipal(idx)}
                          disabled={loading}
                          title="Establecer como principal"
                        >★</button>
                      )}
                      <button
                        type="button"
                        className="imagen-accion-btn imagen-btn-mover"
                        onClick={() => moverImagen(idx, -1)}
                        disabled={loading || idx === 0}
                        title="Mover a la izquierda"
                      >‹</button>
                      <button
                        type="button"
                        className="imagen-accion-btn imagen-btn-mover"
                        onClick={() => moverImagen(idx, 1)}
                        disabled={loading || idx === imagenes.length - 1}
                        title="Mover a la derecha"
                      >›</button>
                      <button
                        type="button"
                        className="imagen-accion-btn imagen-btn-remove"
                        onClick={() => eliminarImagen(idx)}
                        disabled={loading}
                        title="Eliminar imagen"
                      >✕</button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Área de subida */}
            <div
              className={`file-upload-area ${loading ? 'disabled' : ''}`}
              onClick={() => !loading && fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/jpeg,image/png,image/webp"
                onChange={handleImagenesChange}
                disabled={loading}
                style={{ display: 'none' }}
              />
              <span className="file-upload-icon">📷</span>
              <span className="file-upload-text">Clic para agregar imágenes</span>
              <span className="file-upload-hint">JPG · PNG · WEBP — múltiples archivos permitidos</span>
            </div>

            {/* URL legacy opcional */}
            <div className="imagen-url-legacy">
              <label htmlFor="imagen" className="imagen-url-legacy-label">
                ↳ O ingresá una URL directa (opcional):
              </label>
              <input
                type="url" id="imagen" name="imagen"
                value={formData.imagen} onChange={handleChange}
                placeholder="https://ejemplo.com/imagen.jpg"
                disabled={loading}
              />
            </div>
          </div>

          {/* Progreso */}
          {uploadProgress && (
            <div className="form-message form-upload">
              <span className="spinner spinner-dark"></span>
              <span>{uploadProgress}</span>
            </div>
          )}

          {/* Error */}
          {error && <div className="form-message form-error"><span>⚠️ {error}</span></div>}

          {/* Éxito */}
          {success && <div className="form-message form-success"><span>✓ {success}</span></div>}

          {/* Botones */}
          <div className="editor-buttons">
            <button type="button" className="btn-cancel" onClick={handleCancel} disabled={loading}>Cancelar</button>
            <button type="submit" className="btn-save" disabled={loading}>
              {loading
                ? (<><span className="spinner"></span>{uploadProgress ? 'Subiendo...' : 'Guardando...'}</>)
                : 'Guardar Cambios'
              }
            </button>
          </div>

        </form>
      </div>
    </div>
  );
};

export default EditorProducto;
